import { loadStripe } from "@stripe/stripe-js";
import { keepPreviousData } from "@tanstack/react-query";
import { differenceInCalendarDays } from "date-fns";
import { ArrowDown, ArrowRight, Building2, Calendar, Check, ChevronDown, ChevronRight, Clock, CreditCard, Gift, Plus, Tag, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import TopProgressBar from "~/components/new/TopProgressBar";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { useScuid } from "~/hooks/use-scuid";
import { type CartItem, getCartItemTotalDuration, getCartItemTotalPrice, getCartItemGtmTotalPrice, getEntirePurchaseDiscountAmount, getPerItemDiscountAmounts, type MatchedDiscountCode, toGtmCartItems, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";
import { GTMEvent } from "../../components/gtmProvider/GTMContext";
import useGTM from "../../components/gtmProvider/useGTM";
import { isEmailBlockedFromBooking } from "../../helper/common/blockedEmails";
import toFixedNumber from "../../helper/common/toFixedNumber";
import { KLARNA_MIN_CART_TOTAL_EUR } from "~/lib/klarna";
import { isEmbedded, postToParent, rememberEmbedReturnPage, takeDrawerHandover } from "~/lib/embed";


const KLARNA_BILLING_COUNTRY = 'DE';

const getCartTotalAfterDiscount = (cartItems: CartItem[], matchedDiscountCode: MatchedDiscountCode | null) => {
    const subtotalPrice = cartItems.reduce((current, item) => current + getCartItemTotalPrice(item), 0);
    const displaySubtotalPrice = cartItems.reduce((current, item) => current + (item.isBundleRedemption ? 0 : getCartItemTotalPrice(item)), 0);
    const perItemDiscount = getPerItemDiscountAmounts(cartItems, matchedDiscountCode).reduce((sum, amount) => sum + amount, 0);
    const entirePurchaseDiscount = getEntirePurchaseDiscountAmount(subtotalPrice, matchedDiscountCode);
    return displaySubtotalPrice - perItemDiscount - entirePurchaseDiscount;
};


const ScrollMoreHint = () => {
    const { language } = useLanguageStore();
    const [isAtBottom, setIsAtBottom] = useState(true);

    useEffect(() => {
        const check = () => {
            setIsAtBottom(window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 16);
        };

        check();
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check, { passive: true });
        const resizeObserver = new ResizeObserver(check);
        resizeObserver.observe(document.body);

        return () => {
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            resizeObserver.disconnect();
        };
    }, []);

    const handleClick = () => {
        window.scrollBy({ top: window.innerHeight * 0.7, behavior: 'smooth' });
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            aria-label={language === Language.GERMAN ? 'mehr anzeigen' : 'show more'}
            className={`absolute left-1/2 -translate-x-1/2 -top-5 inline-flex items-center justify-center rounded-full bg-white p-2 text-burgundy shadow-[0_2px_8px_rgba(86,28,44,0.2)] transition-opacity duration-200 ${isAtBottom ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
            <ArrowDown className='w-4 h-4' />
        </button>
    );
}

const FixedBottomBar = () => {
    const {
        selectedStore,
        selectedTimeslot,
        cartItems,
        firstName,
        lastName,
        email,
        phoneDialCode,
        phone,
        messageFromCustomer,
        discountCode,
        wantsEarlierTimeslot,
        preferredContactTimes,
        deeplinkPractitionerId,
        preferredPractitionerId,
        paymentMethod,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            cartItems: state.cartItems,
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            phoneDialCode: state.phoneDialCode,
            phone: state.phone,
            messageFromCustomer: state.messageFromCustomer,
            discountCode: state.discountCode,
            wantsEarlierTimeslot: state.wantsEarlierTimeslot,
            preferredContactTimes: state.preferredContactTimes,
            deeplinkPractitionerId: state.deeplinkPractitionerId,
            preferredPractitionerId: state.preferredPractitionerId,
            paymentMethod: state.paymentMethod,
        }))
    );

    const bookAppointmentMutation = trpc.publicAppointments.bookAppointment.useMutation();
    const createKlarnaCheckoutMutation = trpc.publicPayments.createKlarnaCheckout.useMutation();
    const { isAuthenticated } = useCustomerAuthStore();

    const { sendEvent } = useGTM();
    const navigate = useNavigate();
    const getScuid = useScuid();

    const { t, language } = useLanguageStore();

    const [isEmailBlocked, setIsEmailBlocked] = useState(false);

    const isSubmittingClinicBookingRef = useRef(false);
    const [isSubmittingClinicBooking, setIsSubmittingClinicBooking] = useState(false);

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const buildBookableItems = () => cartItems.map(item => ({
        bookableItemId: item.bookableItemId,
        bookableItemType: item.bookableItemType as 'SERVICE' | 'BUNDLE',
        treatmentVariantId: item.selectedVariantId,
        addOns: item.addOns.map(addon => ({
            addOnId: addon.addOnId,
            variantId: addon.variantId,
        })),
        bundleId: item.bundleId,
        isBundleRedemption: item.isBundleRedemption ?? false,
    }));

    const sendGTMEvent = (result: { appointmentId: string; isFirstBooking?: boolean }) => {
        sendEvent(GTMEvent.BOOKING_FUNNEL_BOOKED, {
            is_first_booking: result.isFirstBooking === true,
            transaction_id: result.appointmentId,
            appointment: {
                datetime: selectedTimeslot?.getDisplayDateTime(),
                notify_about_earlier_possible_appointment: false,
            },
            total_net: toFixedNumber(
                cartItems
                    .map((item) => getCartItemGtmTotalPrice(item) / 1.19)
                    .reduce((acc, netPrice) => (acc || 0) + (netPrice || 0)),
            ),
            total_gross: toFixedNumber(
                cartItems
                    .map((item) => getCartItemGtmTotalPrice(item))
                    .reduce((acc, netPrice) => (acc || 0) + (netPrice || 0)),
            ),
            coupon: discountCode,
            cart_items: toGtmCartItems(cartItems),
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: {
                id: null,
                has_account: false,
                email: email.toLowerCase(),
                phone: phone,
            },
            payment_method: 'clinic',
        });
    };

    const handleClinicPayment = async () => {
        if (!selectedTimeslot || !selectedStore) {
            toast.error(t('bookingErrorInvalidSelectedData'));
            return;
        }
        if (isSubmittingClinicBookingRef.current) {
            return;
        }
        // Set synchronously so a double click can't start a second booking while the scuid is being resolved.
        isSubmittingClinicBookingRef.current = true;
        setIsSubmittingClinicBooking(true);

        try {
            const constrainedPractitionerId = deeplinkPractitionerId ?? (isAuthenticated() ? preferredPractitionerId : null);
            const result = await bookAppointmentMutation.mutateAsync({
                storeId: selectedStore.id ?? -1,
                selectedStartTime: selectedTimeslot.toJson(),
                bookableItems: buildBookableItems(),
                email: email.trim().toLowerCase(),
                firstName,
                lastName,
                phone: phoneDialCode + phone.replaceAll(" ", ""),
                couponCodes: discountCode,
                wantsEarlierTimeslot,
                preferredContactTime: preferredContactTimes.length === 0 ? 'No Call-Time Preference' : preferredContactTimes.sort().join(", "),
                message: messageFromCustomer,
                scuid: await getScuid(),
                ...(constrainedPractitionerId ? { practitionerId: constrainedPractitionerId } : {}),
                ...(isAuthenticated() && preferredPractitionerId && !deeplinkPractitionerId ? { preferredPractitionerId } : {}),
            });

            sendGTMEvent(result);
            navigate("/confirmation", { state: { selectedStore } });
        } finally {
            isSubmittingClinicBookingRef.current = false;
            setIsSubmittingClinicBooking(false);
        }
    };

    const handleKlarnaPayment = async () => {
        if (!selectedTimeslot || !selectedStore) {
            toast.error(t('bookingErrorInvalidSelectedData'));
            return;
        }

        const returnUrl = `${window.location.origin}/klarna-return`;

        const result = await createKlarnaCheckoutMutation.mutateAsync({
            storeId: selectedStore.id ?? -1,
            selectedStartTime: selectedTimeslot.toJson(),
            bookableItems: buildBookableItems(),
            email: email.trim().toLowerCase(),
            firstName,
            lastName,
            phone: phoneDialCode + phone.replaceAll(" ", ""),
            couponCodes: discountCode,
            message: messageFromCustomer,
            wantsEarlierTimeslot,
            preferredContactTime: preferredContactTimes.length === 0 ? 'No Call-Time Preference' : preferredContactTimes.sort().join(", "),
            returnUrl,
            language: language === Language.GERMAN ? 'de' : 'en',
            scuid: await getScuid(),
        });

        if (!result.success) {
            toast.error(result.error || (language === Language.GERMAN ? 'Klarna-Zahlung konnte nicht gestartet werden.' : 'Could not start Klarna payment.'));
            return;
        }

        const stripe = await loadStripe(result.publishableKey);
        if (!stripe) {
            toast.error(language === Language.GERMAN ? 'Zahlung konnte nicht initialisiert werden.' : 'Could not initialize payment.');
            return;
        }

        // Klarna refuses to be framed, so when embedded the parent page has to perform the redirect.
        const embedded = isEmbedded();
        const { error, paymentIntent } = await stripe.confirmKlarnaPayment(result.clientSecret, {
            payment_method: {
                billing_details: {
                    name: `${firstName} ${lastName}`.trim(),
                    email: email.trim().toLowerCase(),
                    phone: phoneDialCode + phone.replaceAll(" ", ""),
                    address: { country: KLARNA_BILLING_COUNTRY },
                },
            },
            return_url: `${returnUrl}?klarna_auth_id=${result.klarnaAuthorizationId}`,
        }, { handleActions: !embedded });

        if (error) {
            toast.error(error.message || (language === Language.GERMAN ? 'Klarna-Zahlung fehlgeschlagen.' : 'Klarna payment failed.'));
            return;
        }

        if (embedded) {
            const redirectUrl = paymentIntent?.next_action?.redirect_to_url?.url;
            if (!redirectUrl) {
                toast.error(language === Language.GERMAN ? 'Klarna-Zahlung konnte nicht gestartet werden.' : 'Could not start Klarna payment.');
                return;
            }
            rememberEmbedReturnPage();
            postToParent("redirect", { url: redirectUrl });
        }
    };

    const handleClick = async () => {
        if (isEmailBlockedFromBooking(email)) {
            setIsEmailBlocked(true);
            toast.error(t('bookingErrorEmailBlocked'));
            return;
        }
        setIsEmailBlocked(false);

        try {
            if (paymentMethod === 'klarna') {
                await handleKlarnaPayment();
            } else {
                await handleClinicPayment();
            }
        } catch (e: any) {
            console.log(e);
            if (e.message === "The desired slot is not available anymore.") {
                toast.error(t('bookingErrorTheDesiredSlotIsNotAvailableAnymore'));
            } else {
                toast.error(t('bookingErrorUnknown'));
            }
        }
    };

    const isLoading = isSubmittingClinicBooking || bookAppointmentMutation.isPending || createKlarnaCheckoutMutation.isPending;

    const bookNowText = language === Language.GERMAN
        ? (cmsConfig?.bookNowButtonTextDe ?? 'JETZT BUCHEN')
        : (cmsConfig?.bookNowButtonTextEn ?? 'BOOK NOW');

    return (
        <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-4 p-6 pb-8 bg-light-burgundy">
            <ScrollMoreHint />
            {isEmailBlocked && (
                <span className="text-sm font-light text-red-600">
                    {t('bookingErrorEmailBlocked')}
                </span>
            )}
            <PrimaryActionButton
                onClick={handleClick}
                label={bookNowText}
                disabled={cartItems.length === 0}
                loading={isLoading}
                rightIcon={<ArrowRight />}
            />
        </div>
    )
}

const BookingSummaryCard = () => {
    const { cartItems, selectedStore, selectedTimeslot, firstName, lastName } = useBookingFlowStore(
        useShallow((state) => ({
            cartItems: state.cartItems,
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            firstName: state.firstName,
            lastName: state.lastName,
        }))
    );

    const { language } = useLanguageStore();

    if (!selectedStore) {
        return null;
    }

    const totalDurationMinutes = cartItems.reduce((current, item) => current + getCartItemTotalDuration(item), 0);
    const startTime = selectedTimeslot;
    const endTime = startTime?.clone().addMinutes(totalDurationMinutes);

    const dateStr = startTime?.format("EEEE, d. LLLL yyyy", { locale: getDateLocaleByLocale(language) });
    const timeOfDayStr = startTime && endTime
        ? startTime.format("HH:mm", { locale: getDateLocaleByLocale(language) }) + " - " + endTime.format("HH:mm", { locale: getDateLocaleByLocale(language) }) + " Uhr"
        : null;
    const customerName = `${firstName} ${lastName}`.trim();

    const chips = [
        { icon: Calendar, text: dateStr },
        { icon: Clock, text: timeOfDayStr },
        { icon: User, text: customerName },
    ].filter((chip): chip is { icon: typeof Calendar; text: string } => !!chip.text);

    const storeImage = selectedStore.images?.[0];

    return (
        <div className='-mt-2 pt-8 pb-6 px-6 bg-white rounded-b-xl shadow-[0_4px_12px_-6px_rgba(86,28,44,0.25)]'>
            <div className='max-w-2xl mx-auto flex flex-col gap-4'>
                <div className='flex flex-row items-center gap-4'>
                    {storeImage && (
                        <img
                            className='w-14 h-14 shrink-0 object-cover rounded-md'
                            src={storeImage}
                            alt={selectedStore.name}
                        />
                    )}
                    <div className='flex flex-col gap-1 min-w-0'>
                        <span className='text-sm font-semibold text-burgundy'>
                            Kalia Lab {selectedStore.name}
                        </span>
                        <span className='text-sm font-light text-burgundy'>
                            {[selectedStore.address, selectedStore.zipCode].filter(Boolean).join(', ')}
                        </span>
                    </div>
                </div>

                {chips.length > 0 && (
                    <div className='flex flex-row flex-wrap gap-2'>
                        {chips.map((chip, index) => {
                            const Icon = chip.icon;
                            return (
                                <div key={index} className='inline-flex items-center gap-1.5 rounded-full bg-burgundy/10 px-3 py-1.5'>
                                    <Icon className='w-4 h-4 text-burgundy shrink-0' strokeWidth={2.5} />
                                    <span className='text-xs font-light text-burgundy'>{chip.text}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

const ServicesSection = () => {
    const { cartItems, matchedDiscountCode, discountCode } = useBookingFlowStore(
        useShallow((state) => ({
            cartItems: state.cartItems,
            matchedDiscountCode: state.matchedDiscountCode,
            discountCode: state.discountCode,
        }))
    );

    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const subtotalPrice = cartItems.reduce((current, item) => current + getCartItemTotalPrice(item), 0);
    const displaySubtotalPrice = cartItems.reduce((current, item) => current + (item.isBundleRedemption ? 0 : getCartItemTotalPrice(item)), 0);
    const perItemDiscount = getPerItemDiscountAmounts(cartItems, matchedDiscountCode).reduce((current, amount) => current + amount, 0);
    const entirePurchaseDiscount = getEntirePurchaseDiscountAmount(subtotalPrice, matchedDiscountCode);
    const totalDiscount = perItemDiscount + entirePurchaseDiscount;
    const totalAfterDiscount = subtotalPrice - totalDiscount;
    const displayTotalAfterDiscount = displaySubtotalPrice - totalDiscount;

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price);

    return (
        <div className='flex flex-col gap-3'>
            <span className='text-lg font-normal text-burgundy'>
                {language === Language.GERMAN ? (cmsConfig?.treatmentsTitleDe ?? 'Behandlungen') : (cmsConfig?.treatmentsTitleEn ?? 'Treatments')}
            </span>

            <div className='flex flex-col gap-3'>
                {cartItems.map((item, index) => {
                    const isRedemption = !!item.isBundleRedemption;
                    const isBundle = item.bookableItemType === 'BUNDLE' && item.bundleId && !isRedemption;
                    const bundleTotalPrice = isRedemption && item.bundleUnitPriceGross
                        ? item.bundleUnitPriceGross
                        : isBundle && item.bundleUnitPriceGross && item.bundleUnitQuantity
                            ? item.bundleUnitPriceGross * item.bundleUnitQuantity
                            : item.price;
                    const displayName = isBundle && item.bundleName
                        ? item.bundleName
                        : item.selectedVariantName;

                    return (
                        <div key={index} className='flex flex-col gap-1'>
                            {/* Main item */}
                            <div className='flex flex-row items-center justify-between'>
                                <div className='flex items-center gap-2'>
                                    {(isBundle || isRedemption) && (
                                        <Gift className='w-4 h-4 text-burgundy shrink-0' />
                                    )}
                                    <span className='text-sm text-burgundy font-light'>
                                        {displayName}
                                    </span>
                                </div>
                                <span className='text-sm text-burgundy font-light'>
                                    {isRedemption ? '0 EUR' : `${formatPrice(bundleTotalPrice)} EUR`}
                                </span>
                            </div>
                            {/* Bundle details - show per-session price */}
                            {isBundle && item.bundleUnitPriceGross && item.bundleUnitQuantity && (
                                <div className='ml-6 text-xs text-burgundy/60 font-light'>
                                    ({item.bundleUnitQuantity} × {formatPrice(item.bundleUnitPriceGross)} EUR {language === Language.GERMAN ? 'pro Sitzung' : 'per session'})
                                </div>
                            )}
                            {/* Bundle redemption - single unit price */}
                            {isRedemption && (
                                <div className='ml-6 text-xs text-burgundy/60 font-light'>
                                    ({language === Language.GERMAN ? 'Bundle-Einlösung' : 'Bundle redemption'})
                                </div>
                            )}
                            {/* Add-ons */}
                            {item.addOns.length > 0 && (
                                <div className='flex flex-col gap-1 ml-4'>
                                    {item.addOns.map((addon) => (
                                        <div key={addon.addOnId} className='flex flex-row items-center justify-between'>
                                            <span className='text-xs text-burgundy/70 font-light'>
                                                + {language === Language.GERMAN ? addon.addOnNameDe : addon.addOnNameEn}
                                            </span>
                                            <span className='text-xs text-burgundy/70 font-light'>
                                                {formatPrice(addon.price)} EUR
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                {totalDiscount > 0 && (
                    <div className='flex flex-row items-center justify-between'>
                        <span className='text-sm font-light text-green-700'>
                            {language === Language.GERMAN ? 'Rabatt' : 'Discount'}{discountCode ? ` ${discountCode}` : ''}
                        </span>
                        <span className='text-sm font-light text-green-700'>
                            -{formatPrice(totalDiscount)} EUR
                        </span>
                    </div>
                )}
            </div>

            <div className='h-px bg-burgundy w-full' />

            <div className='flex flex-row items-center justify-between'>
                <span className='text-sm font-semibold text-burgundy'>
                    {language === Language.GERMAN ? (cmsConfig?.totalLabelDe ?? 'Gesamt') : (cmsConfig?.totalLabelEn ?? 'Total')}
                </span>
                <span className='text-sm font-semibold text-burgundy'>
                    {formatPrice(displayTotalAfterDiscount)} EUR
                </span>
            </div>

            <div className='mt-2'>
                <DiscountCodeSection />
            </div>
        </div>
    );
}

const MessageSection = () => {
    const { messageFromCustomer, setMessageFromCustomer } = useBookingFlowStore(
        useShallow((state) => ({
            messageFromCustomer: state.messageFromCustomer,
            setMessageFromCustomer: state.setMessageFromCustomer,
        }))
    );

    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const [isOpen, setIsOpen] = useState(false);
    const messageBeforeEditRef = useRef(messageFromCustomer);

    const label = language === Language.GERMAN ? 'Nachricht hinzufügen (optional)' : 'Add message (optional)';
    const savedMessage = messageFromCustomer.trim();

    const handleOpen = () => {
        messageBeforeEditRef.current = messageFromCustomer;
        setIsOpen(true);
    };

    const handleCancel = () => {
        setMessageFromCustomer(messageBeforeEditRef.current);
        setIsOpen(false);
    };

    const handleSave = () => {
        setMessageFromCustomer(messageFromCustomer.trim());
        setIsOpen(false);
    };

    if (!isOpen && savedMessage) {
        return (
            <div className='flex flex-row items-center gap-3 px-4 py-2.5 rounded-xl border border-burgundy/30 bg-white'>
                <Check className='w-4 h-4 text-burgundy shrink-0' />
                <span className='flex-1 min-w-0 truncate text-sm font-light text-burgundy'>
                    {savedMessage}
                </span>
                <button
                    type="button"
                    onClick={handleOpen}
                    className='shrink-0 text-xs font-light text-burgundy underline underline-offset-2 hover:text-burgundy/70'
                >
                    {language === Language.GERMAN ? 'bearbeiten' : 'edit'}
                </button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col rounded-xl border bg-white transition-colors ${isOpen ? 'border-burgundy' : 'border-burgundy/30'}`}>
            <button
                type="button"
                onClick={isOpen ? handleSave : handleOpen}
                className='flex flex-row items-center gap-3 px-4 py-2.5 text-left'
            >
                <Plus className='w-4 h-4 text-burgundy shrink-0' />
                <span className='flex-1 text-sm font-light text-burgundy'>{label}</span>
                {isOpen
                    ? <ChevronDown className='w-4 h-4 text-burgundy/50 shrink-0' />
                    : <ChevronRight className='w-4 h-4 text-burgundy/50 shrink-0' />
                }
            </button>

            {isOpen && (
                <div className='flex flex-col gap-3 px-4 pb-3'>
                    <Textarea
                        placeholder={language === Language.GERMAN ? (cmsConfig?.messagePlaceholderDe ?? 'Ihre Nachricht an uns...') : (cmsConfig?.messagePlaceholderEn ?? 'Your message to us...')}
                        value={messageFromCustomer}
                        onChange={(e) => setMessageFromCustomer(e.target.value)}
                        autoFocus
                        className="shadow-none border border-burgundy/40 bg-gray-50 text-burgundy md:text-xs placeholder:text-burgundy/50 placeholder:text-sm md:placeholder:text-xs focus-visible:ring-burgundy/50 transition-colors min-h-[80px] rounded-md"
                        rows={3}
                    />
                    <div className='flex flex-row justify-end gap-2'>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className='px-4 py-2 rounded-full border border-burgundy text-xs font-normal text-burgundy hover:bg-burgundy/5 transition-colors'
                        >
                            {language === Language.GERMAN ? 'Abbrechen' : 'Cancel'}
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className='px-4 py-2 rounded-full border border-burgundy bg-burgundy text-xs font-normal text-white hover:bg-burgundy/90 transition-colors'
                        >
                            {language === Language.GERMAN ? 'Speichern' : 'Save'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

type UnmatchedReason = {
    reason: 'NOT_FOUND' | 'DATE_INVALID' | 'CUSTOMER_TYPE_NOT_ELIGIBLE' | 'TIME_WINDOW_INVALID' | 'STORE_NOT_APPLICABLE';
    endDate?: string;
    applicationCustomerType?: string;
};

const getDiscountCodeErrorMessage = ({
    matchedDiscountCode,
    unmatchedReason,
    applicableCount,
    isEntirePurchase,
    language,
}: {
    matchedDiscountCode: MatchedDiscountCode | null;
    unmatchedReason: UnmatchedReason | null;
    applicableCount: number;
    isEntirePurchase: boolean;
    language: Language;
}): string | null => {
    if (unmatchedReason) {
        if (unmatchedReason.reason === 'NOT_FOUND') {
            return language === Language.GERMAN
                ? 'Code ungültig (wird vor Ort geprüft)'
                : 'Code invalid (will be checked on-site)';
        }
        if (unmatchedReason.reason === 'DATE_INVALID' && unmatchedReason.endDate) {
            const formattedDate = new Date(unmatchedReason.endDate).toLocaleDateString(
                language === Language.GERMAN ? 'de-DE' : 'en-US',
                { day: '2-digit', month: '2-digit', year: 'numeric' },
            );
            return language === Language.GERMAN
                ? `Nur gültig für Termine bis zum ${formattedDate}`
                : `Only valid for appointments until ${formattedDate}`;
        }
        if (unmatchedReason.reason === 'CUSTOMER_TYPE_NOT_ELIGIBLE') {
            if (unmatchedReason.applicationCustomerType === 'NEW_CUSTOMERS_ONLY') {
                return language === Language.GERMAN
                    ? 'Nur gültig für Neukunden'
                    : 'Only valid for new customers';
            }
            return language === Language.GERMAN
                ? 'Code ungültig (wird vor Ort geprüft)'
                : 'Code invalid (will be checked on-site)';
        }
        if (unmatchedReason.reason === 'TIME_WINDOW_INVALID') {
            return language === Language.GERMAN
                ? 'Dieser Rabattcode ist nur an bestimmten Tagen/Uhrzeiten gültig'
                : 'This discount code is only valid on specific days/times';
        }
        if (unmatchedReason.reason === 'STORE_NOT_APPLICABLE') {
            return language === Language.GERMAN
                ? 'Dieser Rabattcode ist an diesem Standort nicht gültig'
                : 'This discount code is not valid at this location';
        }
    }

    if (matchedDiscountCode && !isEntirePurchase && applicableCount === 0) {
        return language === Language.GERMAN
            ? 'Nicht gültig für die ausgewählten Leistungen'
            : 'Not valid for selected treatments';
    }

    return null;
};

const DiscountCodeSection = () => {
    const { email, discountCode, setDiscountCode, matchedDiscountCode, setMatchedDiscountCode, cartItems, selectedTimeslot, selectedStore } = useBookingFlowStore(
        useShallow((state) => ({
            email: state.email,
            discountCode: state.discountCode,
            setDiscountCode: state.setDiscountCode,
            matchedDiscountCode: state.matchedDiscountCode,
            setMatchedDiscountCode: state.setMatchedDiscountCode,
            cartItems: state.cartItems,
            selectedTimeslot: state.selectedTimeslot,
            selectedStore: state.selectedStore,
        }))
    );

    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isApplied, setIsApplied] = useState(!!discountCode);
    const [isValidating, setIsValidating] = useState(false);
    const [unmatchedReason, setUnmatchedReason] = useState<UnmatchedReason | null>(null);

    const utils = trpc.useUtils();

    const validateAndApplyCode = async (code: string) => {
        if (!selectedTimeslot) return;
        setIsValidating(true);
        try {
            const bookableItemIds = cartItems
                .filter((item) => !item.isBundleRedemption)
                .map((item) => item.bookableItemId);
            const bundleIds = cartItems
                .filter((item) => item.bundleId && !item.isBundleRedemption)
                .map((item) => item.bundleId!);
            const result = await utils.publicBookingFunnel.validateDiscountCode.fetch({
                code,
                bookableItemIds,
                bundleIds,
                appointmentDate: selectedTimeslot.toUTC().toISOString(),
                customerEmail: email,
                storeId: selectedStore?.id,
            });

            if (result.matched) {
                setMatchedDiscountCode({
                    id: result.discountCode.id,
                    name: result.discountCode.name,
                    type: result.discountCode.type as 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT',
                    discountValue: result.discountCode.discountValue,
                    applicationLevel: result.discountCode.applicationLevel as 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE',
                    applicableBookableItemIds: result.discountCode.applicableBookableItemIds,
                    applicableBundleIds: result.discountCode.applicableBundleIds,
                });
                setUnmatchedReason(null);
            } else {
                setMatchedDiscountCode(null);
                setUnmatchedReason({
                    reason: result.reason,
                    endDate: 'endDate' in result ? result.endDate : undefined,
                    applicationCustomerType: 'applicationCustomerType' in result ? result.applicationCustomerType : undefined,
                });
            }
        } catch {
            setMatchedDiscountCode(null);
            setUnmatchedReason({ reason: 'NOT_FOUND' });
        } finally {
            setIsValidating(false);
        }
    };

    useEffect(() => {
        if (discountCode) {
            setInputValue(discountCode);
            setIsApplied(true);
            validateAndApplyCode(discountCode);
        }
    }, []);

    const handleApply = async () => {
        const trimmedCode = inputValue.trim();
        if (!trimmedCode) return;
        setDiscountCode(trimmedCode);
        setIsApplied(true);
        await validateAndApplyCode(trimmedCode);
    };

    const handleRemove = () => {
        setDiscountCode('');
        setMatchedDiscountCode(null);
        setUnmatchedReason(null);
        setInputValue('');
        setIsApplied(false);
        setIsOpen(false);
    };

    const formatDiscountLabel = () => {
        if (!matchedDiscountCode) {
            return null;
        }
        if (matchedDiscountCode.type === 'PERCENTAGE_DISCOUNT') {
            return `${matchedDiscountCode.discountValue}% ${language === Language.GERMAN ? 'Rabatt' : 'discount'}`;
        }
        return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(matchedDiscountCode.discountValue)} EUR ${language === Language.GERMAN ? 'Rabatt' : 'discount'}`;
    };

    const applicableCount = matchedDiscountCode
        ? cartItems.filter((item) =>
            !item.isBundleRedemption &&
            (matchedDiscountCode.applicableBookableItemIds.includes(item.bookableItemId) ||
            (item.bundleId && matchedDiscountCode.applicableBundleIds.includes(item.bundleId)))
        ).length
        : 0;

    const isEntirePurchase = matchedDiscountCode?.applicationLevel === 'ENTIRE_PURCHASE';
    const isValidDiscount = !!matchedDiscountCode && (isEntirePurchase || applicableCount > 0);

    const removeButton = (className: string) => (
        <button
            type="button"
            onClick={handleRemove}
            className={`shrink-0 text-xs font-light underline underline-offset-2 transition-colors ${className}`}
        >
            {language === Language.GERMAN ? 'entfernen' : 'remove'}
        </button>
    );

    if (isApplied && !isValidating && isValidDiscount) {
        return (
            <div className='flex flex-col gap-1 px-4 py-2.5 rounded-xl border border-green-600/40 bg-green-50'>
                <div className='flex flex-row items-center gap-3'>
                    <Check className='w-4 h-4 text-green-700 shrink-0' />
                    <span className='flex-1 min-w-0 text-sm font-light text-green-700'>
                        <span className='font-semibold'>{discountCode}</span>
                        {' '}{language === Language.GERMAN ? 'wurde angewandt' : 'has been applied'}
                    </span>
                    {removeButton('text-green-700 hover:text-green-800')}
                </div>
                <span className='pl-7 text-xs font-light text-green-700/70'>
                    {formatDiscountLabel()}
                    {' '}&middot;{' '}
                    {isEntirePurchase
                        ? (language === Language.GERMAN ? 'gültig für die gesamte Buchung' : 'applies to entire purchase')
                        : (language === Language.GERMAN
                            ? `gültig für ${applicableCount} ${applicableCount === 1 ? 'Leistung' : 'Leistungen'}`
                            : `applies to ${applicableCount} ${applicableCount === 1 ? 'service' : 'services'}`)
                    }
                </span>
            </div>
        );
    }

    if (isApplied) {
        const errorMessage = isValidating
            ? null
            : getDiscountCodeErrorMessage({
                matchedDiscountCode,
                unmatchedReason,
                applicableCount,
                isEntirePurchase,
                language,
            });

        return (
            <div className='flex flex-col gap-1 px-4 py-2.5 rounded-xl border border-burgundy/30 bg-white'>
                <div className='flex flex-row items-center gap-3'>
                    <Tag className='w-4 h-4 text-burgundy shrink-0' />
                    <span className='flex-1 min-w-0 text-sm font-semibold text-burgundy'>
                        {discountCode}
                        {isValidating && <span className='font-light text-burgundy/60'> ...</span>}
                    </span>
                    {removeButton('text-burgundy hover:text-burgundy/70')}
                </div>
                {errorMessage && (
                    <span className='pl-7 text-xs font-light text-red-600'>
                        {errorMessage}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className={`flex flex-col rounded-xl border bg-white transition-colors ${isOpen ? 'border-burgundy' : 'border-burgundy/30'}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className='flex flex-row items-center gap-3 px-4 py-2.5 text-left'
            >
                <Tag className='w-4 h-4 text-burgundy shrink-0' />
                <span className='flex-1 text-sm font-light text-burgundy'>
                    {language === Language.GERMAN ? (cmsConfig?.discountCodeTitleDe ?? 'Rabattcode') : (cmsConfig?.discountCodeTitleEn ?? 'Discount code')}
                </span>
                {isOpen
                    ? <ChevronDown className='w-4 h-4 text-burgundy/50 shrink-0' />
                    : <ChevronRight className='w-4 h-4 text-burgundy/50 shrink-0' />
                }
            </button>

            {isOpen && (
                <div className='px-4 pb-3'>
                    <div className='relative'>
                        <Input
                            type="text"
                            placeholder={language === Language.GERMAN ? (cmsConfig?.discountCodePlaceholderDe ?? 'Rabattcode eingeben') : (cmsConfig?.discountCodePlaceholderEn ?? 'Enter discount code')}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && inputValue.trim()) {
                                    handleApply();
                                }
                            }}
                            autoFocus
                            className={`shadow-none h-9 pl-4 pr-24 rounded-full border bg-transparent font-light text-burgundy placeholder:text-burgundy/50 placeholder:text-sm md:placeholder:text-xs focus-visible:ring-0 transition-colors ${inputValue.trim() ? 'border-burgundy' : 'border-burgundy/40 focus-visible:border-burgundy'}`}
                        />
                        <button
                            type="button"
                            onClick={handleApply}
                            disabled={!inputValue.trim()}
                            className='absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-burgundy hover:text-burgundy/70 disabled:text-burgundy/40 disabled:cursor-not-allowed transition-colors'
                        >
                            {language === Language.GERMAN ? (cmsConfig?.discountCodeButtonTextDe ?? 'ANWENDEN') : (cmsConfig?.discountCodeButtonTextEn ?? 'APPLY')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const AdditionalOptionsSection = () => {
    const { language } = useLanguageStore();

    return (
        <div className='flex flex-col gap-3'>
            <span className='text-sm font-normal text-burgundy'>
                {language === Language.GERMAN ? 'Weitere Optionen' : 'Additional options'}
            </span>
            <MessageSection />
        </div>
    );
}

const PaymentMethodSection = () => {
    const { selectedTimeslot, paymentMethod, setPaymentMethod, cartItems, matchedDiscountCode } = useBookingFlowStore(
        useShallow((state) => ({
            selectedTimeslot: state.selectedTimeslot,
            paymentMethod: state.paymentMethod,
            setPaymentMethod: state.setPaymentMethod,
            cartItems: state.cartItems,
            matchedDiscountCode: state.matchedDiscountCode,
        }))
    );

    const { language } = useLanguageStore();

    const isWithinDateRange = (() => {
        if (!selectedTimeslot) return false;
        const appointmentDate = selectedTimeslot.toUTC();
        const daysUntilAppointment = differenceInCalendarDays(appointmentDate, new Date());
        return daysUntilAppointment <= 27;
    })();
    const meetsMinimumTotal = getCartTotalAfterDiscount(cartItems, matchedDiscountCode) >= KLARNA_MIN_CART_TOTAL_EUR;
    const isKlarnaEligible = isWithinDateRange && meetsMinimumTotal;

    useEffect(() => {
        if (!isKlarnaEligible && paymentMethod === 'klarna') {
            setPaymentMethod('clinic');
        }
    }, [isKlarnaEligible, paymentMethod, setPaymentMethod]);

    const options: { id: 'clinic' | 'klarna'; icon: typeof Building2; titleDe: string; titleEn: string; descDe: string; descEn: string; disabled: boolean }[] = [
        {
            id: 'clinic',
            icon: Building2,
            titleDe: 'Bezahlung in der Klinik',
            titleEn: 'Pay in clinic',
            descDe: 'Zahle bequem vor Ort nach Deiner Behandlung.',
            descEn: 'Pay at the clinic during your appointment.',
            disabled: false,
        },
        {
            id: 'klarna',
            icon: CreditCard,
            titleDe: 'Später zahlen mit Klarna',
            titleEn: 'Pay later with Klarna',
            descDe: isKlarnaEligible
                ? 'Jetzt verifizieren. Die Abbuchung erfolgt erst nach Deiner Behandlung vor Ort.'
                : !meetsMinimumTotal
                    ? `Nur für Buchungen ab ${KLARNA_MIN_CART_TOTAL_EUR} EUR verfügbar.`
                    : 'Nur für Termine innerhalb der nächsten 27 Tage verfügbar.',
            descEn: isKlarnaEligible
                ? 'Pre-approve now, payment captured after your treatment.'
                : !meetsMinimumTotal
                    ? `Only available for bookings of ${KLARNA_MIN_CART_TOTAL_EUR} EUR or more.`
                    : 'Only available for appointments within the next 27 days.',
            disabled: !isKlarnaEligible,
        },
    ];

    return (
        <div className='flex flex-col gap-3'>
            <span className='text-lg font-normal text-burgundy'>
                {language === Language.GERMAN ? 'Zahlungsmethode' : 'Payment method'}
            </span>

            <div className='flex flex-col gap-3'>
                {options.map((option) => {
                    const Icon = option.icon;
                    const isSelected = paymentMethod === option.id;

                    return (
                        <button
                            key={option.id}
                            type="button"
                            disabled={option.disabled}
                            onClick={() => {
                                if (!option.disabled) {
                                    setPaymentMethod(option.id);
                                }
                            }}
                            className={`
                                flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all
                                ${isSelected
                                    ? 'border-burgundy bg-burgundy/5'
                                    : option.disabled
                                        ? 'border-burgundy/15 bg-white/40 opacity-50 cursor-not-allowed'
                                        : 'border-burgundy/20 bg-white/40 hover:border-burgundy/50 cursor-pointer'
                                }
                            `}
                        >
                            <div className='flex flex-col gap-1 flex-1 min-w-0'>
                                <div className='flex items-center gap-2'>
                                    <Icon className='w-4 h-4 text-burgundy shrink-0' />
                                    <span className='text-sm font-medium text-burgundy'>
                                        {language === Language.GERMAN ? option.titleDe : option.titleEn}
                                    </span>
                                </div>
                                <span className='text-xs text-burgundy/70 font-light'>
                                    {language === Language.GERMAN ? option.descDe : option.descEn}
                                </span>
                            </div>

                            <div className={`
                                w-[18px] h-[18px] rounded-full border-[1.5px] shrink-0 flex items-center justify-center
                                ${isSelected ? 'border-burgundy' : 'border-burgundy/60'}
                            `}>
                                {isSelected && (
                                    <div className='w-2.5 h-2.5 rounded-full bg-burgundy' />
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const BookingReview = () => {
    const {
        selectedStore,
        selectedTimeslot,
        cartItems,
        email,
        phone,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            cartItems: state.cartItems,
            email: state.email,
            phone: state.phone,
        }))
    );

    const { sendEvent } = useGTM();
    const navigate = useNavigate();

    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
    });
    const cmsConfig = cmsConfigData?.config;

    const { data: store } = trpc.publicStores.getOne.useQuery({
        id: selectedStore?.id ?? -1,
    }, {
        enabled: !!selectedStore,
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        window.scroll(0, 0);
        sendEvent(GTMEvent.BOOKING_FUNNEL_CONTACT_DETAILS, {
            appointment: {
                datetime: selectedTimeslot?.toUTC().toISOString(),
                notify_about_earlier_possible_appointment: false,
            },
            cart_items: toGtmCartItems(cartItems),
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: {
                id: null,
                has_account: false,
                email: email.toLowerCase(),
                phone: phone,
            },
        });
    }, []);

    useEffect(() => {
        if (!selectedStore) {
            navigate("/");
        }
    }, [selectedStore]);

    // Set when the standalone funnel sends the customer back into the drawer after a cancelled Klarna payment.
    useEffect(() => {
        const errorMessage = takeDrawerHandover<{ errorMessage?: string } | null>('/review-booking')?.errorMessage;
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }, []);


    return (
        <>
            <TopProgressBar onClickBackButton={() => navigate('/enter-details')} percentageFilled={100} displayString={language === Language.GERMAN ? (cmsConfig?.progressDisplayDe ?? 'DETAILS BESTÄTIGEN') : (cmsConfig?.progressDisplayEn ?? 'CONFIRM DETAILS')} color="primary" />
            <div className='min-h-screen pb-64'>
                <FixedBottomBar />

                <BookingSummaryCard />

                <div className="max-w-2xl mx-auto w-full px-6 pt-8 flex flex-col gap-10">
                    <ServicesSection />
                    <div className='-mt-4'>
                        <PaymentMethodSection />
                    </div>
                    <AdditionalOptionsSection />
                </div>
            </div>
        </>
    );
};

export default BookingReview;
